import { fmtNum, fmtTimeNL } from '@/features/ah/utils';
import type { PrijsfavorietenPart } from '../report';
import { calendarWeekNumbers, fmtRange, partStatus } from '../utils';

/*
 * 01 — Campagnedelen: both parts' week strips, then the five headline metrics
 * per week and per part side by side (design "Deel 2 wk 1").
 */

type Totals = PrijsfavorietenPart['totals'];

/*
 * A part's column in the table is the TOTAL for the part, not the sum of its
 * weeks: unique visitors and players are counted once over the whole part, so
 * adding the weeks would count everyone who came back twice. Keyed by the row
 * labels the pipeline emits.
 */
const PART_TOTAL: Record<string, (t: Totals) => number | string> = {
  'Unieke bezoekers': (t) => t.uniqueVisitors,
  'Unieke spelers': (t) => t.uniquePlayers,
  'Aantal bezoeken': (t) => t.visits,
  'Tijd in campagne': (t) => t.timeInCampaign,
  'Games gespeeld': (t) => t.gamesPlayed,
};

function cell(v: number | string | null | undefined): string {
  if (v == null) return '—';
  return typeof v === 'number' ? fmtNum(v) : fmtTimeNL(v);
}

function PartStrip({ part }: { part: PrijsfavorietenPart }) {
  const c = part.campaign;
  const status = partStatus(c);
  const cw = calendarWeekNumbers(c.calendarWeeks);
  return (
    <div className="pf-part">
      <div className="pf-part-head">
        <div className="pf-part-title">
          <span className="pf-part-name">{c.periodLabel}</span>
          <span className="pf-part-sub">
            {c.calendarWeeks} · {fmtRange(c.start, c.end, { year: false })}
          </span>
        </div>
        <span className={`pf-status pf-status-${status.toLowerCase()}`}>{status}</span>
      </div>
      <div className="pf-part-weeks">
        {part.weeks.map((w, i) => {
          const current = status === 'Lopend' && i + 1 === c.currentWeek;
          const planned = w.planned || status === 'Gepland';
          return (
            <div key={w.label} className={`pf-part-week${planned ? ' planned' : ''}`}>
              <span className="pf-part-week-label">
                {w.label}
                {current && ' · lopend'}
              </span>
              <span className="pf-part-week-dates">
                {cw[i] != null && `CW ${cw[i]} · `}
                {w.dates}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function partValues(part: PrijsfavorietenPart, label: string, index: number): (number | string | null)[] {
  const row = part.weekly.rows.find((r) => r.label === label) ?? part.weekly.rows[index];
  // A part that has not started has no figures at all — "—", not a row of zeros.
  const started = part.campaign.currentDay > 0;
  const weeks = part.weeks.map((_w, i) => (started ? (row?.values[i] ?? null) : null));
  const total = started && PART_TOTAL[label] ? PART_TOTAL[label](part.totals) : null;
  return [...weeks, total];
}

export function PartsSection({ previous, current }: { previous: PrijsfavorietenPart; current: PrijsfavorietenPart }) {
  const cols = (p: PrijsfavorietenPart) => [...p.weekly.columns, p.campaign.periodLabel.toUpperCase()];
  const status = partStatus(current.campaign);
  return (
    <div className="section pf-parts">
      <div className="section-head">
        <div>
          <div className="eyebrow">01 — Campagnedelen</div>
          <h2>
            {previous.campaign.periodLabel} &amp; {current.campaign.periodLabel} vergeleken
          </h2>
        </div>
      </div>

      <div className="pf-part-strips">
        <PartStrip part={previous} />
        <PartStrip part={current} />
      </div>

      <div className="pf-compare-scroll">
        <div className="pf-compare-table" role="table" aria-label="Deel 1 en Deel 2 per week">
          <div role="row" className="pf-ct-row pf-ct-groups">
            <span role="columnheader" className="pf-ct-corner" />
            <span role="columnheader" className="pf-ct-group pf-ct-d1">
              {previous.campaign.periodLabel} · {previous.campaign.calendarWeeks}
            </span>
            <span role="columnheader" className="pf-ct-group pf-ct-d2">
              {current.campaign.periodLabel} · {current.campaign.calendarWeeks}
              {status !== 'Afgerond' && ` · ${status}`}
            </span>
          </div>
          <div role="row" className="pf-ct-row pf-ct-head">
            <span role="columnheader">Metriek</span>
            {cols(previous).map((c, i) => (
              <span role="columnheader" key={`d1-${c}`} className={i === cols(previous).length - 1 ? 'pf-ct-total-d1' : ''}>
                {c}
              </span>
            ))}
            {cols(current).map((c, i) => (
              <span role="columnheader" key={`d2-${c}`} className={i === cols(current).length - 1 ? 'pf-ct-total-d2' : 'pf-ct-d2-col'}>
                {c}
              </span>
            ))}
          </div>
          {current.weekly.rows.map((r, idx) => {
            const d1 = partValues(previous, r.label, idx);
            const d2 = partValues(current, r.label, idx);
            return (
              <div role="row" className="pf-ct-row" key={r.label}>
                <span role="rowheader" className="pf-ct-metric">
                  {r.label}
                </span>
                {d1.map((v, i) => (
                  <span role="cell" key={`d1-${i}`} className={i === d1.length - 1 ? 'pf-ct-total-d1' : ''}>
                    {cell(v)}
                  </span>
                ))}
                {d2.map((v, i) => (
                  <span role="cell" key={`d2-${i}`} className={i === d2.length - 1 ? 'pf-ct-total-d2' : 'pf-ct-d2-col'}>
                    {cell(v)}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
